import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import { BsHouse } from 'react-icons/bs';
import { FaChevronUp, FaLaptopMedical, FaRegClipboard, FaRegUser } from 'react-icons/fa';
import { GoLock, GoUnlock } from 'react-icons/go';
import { MdOutlinePhoneAndroid } from 'react-icons/md';

import { medicos } from 'utils/agenda';
import { mascaraCPF, mascaraTelefoneComZero } from 'utils/helpers';
import profissoes from 'utils/profissoes.json';

// Estilos de Labels baseados no sistema legado
const labelBase = { fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.2rem' };
const labelVermelho = { ...labelBase, color: '#dc3545' }; // Principais / Críticos
const labelAzul = { ...labelBase, color: '#0d6efd' }; // Endereço e Médico
const labelCinza = { ...labelBase, color: '#6c757d' }; // Complementares

// Estilos de Texto dos Campos de Entrada
const inputBase = {
  border: '1px solid var(--neutral-200)',
  borderRadius: 10,
  background: 'var(--background-color)',
};
const inputVermelho = { ...inputBase, color: '#dc3545', fontWeight: 600 };
const inputAzul = { ...inputBase, color: '#0d6efd', fontWeight: 600 };

const ESTADOS_BRASIL = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

export default function CamposDadosPaciente({
  register,
  control,
  errors,
  nomeFuncLogado,
  onCepBlur,
  isFetchingCep = false,
  onCpfBlur,
  onCpfChange,
  onCpfRetry,
  isVerifyingCpf = false,
  cpfCheck = {
    status: 'idle',
    pacientes: [],
    lastCpfChecked: '',
    infraError: null,
    message: '',
  },
  isEditing = false,
  agendamentosPaciente = [],
  watch,
  formSessionKey = 'closed',
}) {
  const [editarNome, setEditarNome] = useState(!isEditing);
  const [mostrarInfopac, setMostrarInfopac] = useState(false);
  const infopacTextareaRef = useRef(null);

  const infopacValor = watch?.('INFOPAC') ?? '';
  const dataCadastro = watch?.('dataCadastro') ?? '';
  const dataAtualizacao = watch?.('dataAtualizacao') ?? '';
  const temInfopac = Boolean(String(infopacValor).trim());

  useEffect(() => {
    setMostrarInfopac(false);
  }, [formSessionKey]);

  const toggleInfopac = () => {
    setMostrarInfopac((aberto) => {
      const vaiAbrir = !aberto;
      if (vaiAbrir) {
        requestAnimationFrame(() => {
          infopacTextareaRef.current?.focus();
        });
      }
      return vaiAbrir;
    });
  };

  const infopacField = register('INFOPAC');

  return (
    <>
      <Card
        style={{
          border: '1px solid var(--neutral-200)',
          borderRadius: 20,
          boxShadow: 'none',
          background: '#fff',
          overflow: 'hidden',
          marginBottom: '0.85rem',
        }}
      >
        <Card.Header
          className="d-flex justify-content-between py-2"
          style={{ borderBottom: '1px solid var(--neutral-200)' }}
        >
          <h2 className="fs-6 mb-0 d-flex align-items-center gap-2">
            <FaRegUser aria-hidden="true" />
            Dados do Paciente
          </h2>
          {/* Lado Direito: Metadados / Timestamps */}
          <div
            className="d-flex align-items-center gap-3 text-muted"
            style={{ fontSize: '0.78rem' }}
          >
            <span>
              Cadastro: <strong className="text-dark">{dataCadastro || '-'}</strong>
            </span>
            <span className="border-start ps-3">
              Atualização: <strong className="text-dark">{dataAtualizacao || '-'}</strong>
            </span>
          </div>

          <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--font-gray)' }}>
            Func: <strong>{nomeFuncLogado || '-'}</strong>
          </span>
        </Card.Header>
        <Card.Body className="p-3">
          <Row className="gy-2">
            <Col md={9}>
              <Form.Label style={labelVermelho}>Nome do Paciente *</Form.Label>

              <InputGroup>
                <Form.Control
                  autoFocus
                  {...register('NOMEPAC', { required: 'Informe o nome do paciente.' })}
                  isInvalid={Boolean(errors.NOMEPAC)}
                  disabled={!editarNome} // Desabilita ou habilita conforme o estado
                  style={{
                    ...inputVermelho,
                    textTransform: 'uppercase',
                    backgroundColor: !editarNome ? '#e9ecef' : '#fff', // Cor cinza quando travado
                  }}
                />

                {/* Botão de trava/destrava no lado direito do Input */}
                <Button
                  variant={editarNome ? 'outline-warning' : 'outline-danger'}
                  onClick={() => setEditarNome((prev) => !prev)}
                  title={editarNome ? 'Bloquear edição do nome' : 'Habilitar edição do nome'}
                  type="button"
                >
                  {editarNome ? <GoUnlock size={18} /> : <GoLock size={18} />}
                </Button>

                <Form.Control.Feedback type="invalid">
                  {errors.NOMEPAC?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            <Col md={3}>
              <Form.Label style={labelVermelho}>
                CPF{' '}
                {isVerifyingCpf && (
                  <Spinner
                    animation="border"
                    size="sm"
                    variant="primary"
                    role="status"
                    aria-label="Verificando CPF"
                  />
                )}
              </Form.Label>
              <InputGroup>
                <Controller
                  name="CPFPAC"
                  control={control}
                  render={({ field: { onChange, value, onBlur, ...field } }) => (
                    <Form.Control
                      {...field}
                      value={value || ''}
                      maxLength={14}
                      style={inputVermelho}
                      isInvalid={cpfCheck.status === 'invalid'}
                      onBlur={(e) => {
                        onBlur();
                        onCpfBlur?.(e.target.value);
                      }}
                      onChange={(e) => {
                        const valorFormatado = mascaraCPF(e.target.value);
                        onChange(valorFormatado);
                        onCpfChange?.(valorFormatado);
                      }}
                    />
                  )}
                />
              </InputGroup>
            </Col>

            {cpfCheck.status === 'invalid' && (
              <Col xs={12}>
                <Alert variant="danger" className="py-2 mb-0 small">
                  O CPF digitado é inválido. Verifique os números informados.
                </Alert>
              </Col>
            )}

            {cpfCheck.status === 'infra' && (
              <Col xs={12}>
                <ServiceUnavailableAlert
                  error={cpfCheck.infraError}
                  onRetry={onCpfRetry}
                  stale={false}
                />
              </Col>
            )}

            {cpfCheck.status === 'error' && (
              <Col xs={12}>
                <Alert variant="danger" className="py-2 mb-0 small">
                  <p className="mb-2">
                    {cpfCheck.message ||
                      'Não foi possível verificar se este CPF já está cadastrado.'}
                  </p>
                  <Button type="button" variant="outline-danger" size="sm" onClick={onCpfRetry}>
                    Tentar novamente
                  </Button>
                </Alert>
              </Col>
            )}

            {cpfCheck.status === 'duplicate' && cpfCheck.pacientes.length > 0 && (
              <Col xs={12}>
                <Alert variant="warning" className="py-2 mb-0 small">
                  <Alert.Heading as="h6" className="fs-6 mb-1">
                    Já existe cadastro com este CPF
                  </Alert.Heading>
                  <p className="mb-2">
                    Confira se não é o mesmo paciente antes de salvar. O sistema não permite CPF
                    duplicado em outro cadastro.
                  </p>
                  <ul className="mb-0 ps-3">
                    {cpfCheck.pacientes.map((pac) => (
                      <li key={pac.CODPAC} className="mb-1">
                        <strong>#{pac.CODPAC}</strong> — {pac.NOMEPAC}
                        {pac.dtNasc ? ` · Nasc.: ${pac.dtNasc}` : ''}
                        {pac.CPFPAC ? ` · CPF: ${pac.CPFPAC}` : ''}
                        {pac.CELULARPAC ? ` · Cel.: ${pac.CELULARPAC}` : ''}
                      </li>
                    ))}
                  </ul>
                </Alert>
              </Col>
            )}

            {/*             <Col md={3}>
              <Form.Label style={labelCinza}>RG/Idt</Form.Label>
              <Form.Control {...register('RGPAC')} />
            </Col> */}

            <Col md={3}>
              <Form.Label style={labelVermelho}>Data Nasc.</Form.Label>
              <Form.Control type="date" {...register('DTNPAC')} style={inputVermelho} />
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Nr da Carteirinha</Form.Label>
              <Form.Control {...register('LOCNASCPAC')} style={inputBase} />
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Validade</Form.Label>
              <Form.Control {...register('VALIDADE')} style={inputBase} />
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Profissão</Form.Label>
              <Form.Control
                {...register('PROFISSPAC')}
                list="lista-profissoes"
                placeholder="Selecione ou digite..."
                style={{ ...inputBase, textTransform: 'uppercase' }}
              />
              <datalist id="lista-profissoes">
                {profissoes.map((prof, index) => (
                  <option key={index} value={prof.toUpperCase()} />
                ))}
              </datalist>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ENDEREÇO */}
      <Card
        style={{
          border: '1px solid var(--neutral-200)',
          borderRadius: 20,
          boxShadow: 'none',
          background: '#fff',
          overflow: 'hidden',
          marginBottom: '0.85rem',
        }}
      >
        <Card.Header
          className="d-flex align-items-center py-2"
          style={{ borderBottom: '1px solid var(--neutral-200)' }}
        >
          <h2 className="fs-6 mb-0 d-flex align-items-center gap-2">
            <BsHouse aria-hidden="true" style={{ strokeWidth: 0.5 }} />
            Endereço
          </h2>
        </Card.Header>
        <Card.Body className="p-3">
          <Row className="gy-2">
            <Col md={2}>
              <Form.Label style={labelAzul} className="d-flex align-items-center gap-2">
                CEP {isFetchingCep && <Spinner animation="border" size="sm" variant="primary" />}
              </Form.Label>
              <Form.Control
                {...register('CEPPAC')}
                onBlur={onCepBlur}
                placeholder="00000-000"
                maxLength={9}
                style={inputAzul}
              />
            </Col>

            <Col md={5}>
              <Form.Label style={labelAzul}>Endereço do Paciente</Form.Label>
              <Form.Control
                {...register('ENDRESPAC')}
                style={{ ...inputAzul, textTransform: 'uppercase' }}
              />
            </Col>

            <Col md={2}>
              <Form.Label style={labelAzul}>NR</Form.Label>
              <Form.Control {...register('SETORPAC')} style={inputAzul} />
            </Col>

            <Col md={3}>
              <Form.Label style={labelAzul}>COMPL</Form.Label>
              <Form.Control
                {...register('OBSPAC')}
                placeholder="CASA, APTO, ETC."
                style={{ ...inputAzul, textTransform: 'uppercase' }}
              />
            </Col>

            <Col md={4}>
              <Form.Label style={labelAzul}>Bairro</Form.Label>
              <Form.Control
                {...register('BAIRROPAC')}
                style={{ ...inputAzul, textTransform: 'uppercase' }}
              />
            </Col>

            <Col md={4}>
              <Form.Label style={labelAzul}>Cidade</Form.Label>
              <Form.Control
                {...register('CIDADEPAC')}
                style={{ ...inputAzul, textTransform: 'uppercase' }}
              />
            </Col>

            <Col md={1}>
              <Form.Label style={labelAzul}>UF</Form.Label>
              <Form.Select {...register('ESTADOPAC')} style={inputAzul}>
                <option value="">--</option>
                {ESTADOS_BRASIL.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Empresa</Form.Label>
              <Form.Control
                {...register('EMPRESA')}
                style={{ ...inputBase, textTransform: 'uppercase' }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* CONTATO & SEXO */}
      <Card
        style={{
          border: '1px solid var(--neutral-200)',
          borderRadius: 20,
          boxShadow: 'none',
          background: '#fff',
          overflow: 'hidden',
          marginBottom: '0.85rem',
        }}
      >
        <Card.Header
          className="d-flex align-items-center py-2"
          style={{ borderBottom: '1px solid var(--neutral-200)' }}
        >
          <h2 className="fs-6 mb-0 d-flex align-items-center gap-2">
            <MdOutlinePhoneAndroid aria-hidden="true" />
            Contato
          </h2>
        </Card.Header>
        <Card.Body className="p-3">
          <Row className="gy-2">
            <Col md={3}>
              <Form.Label style={labelVermelho}>Sexo</Form.Label>
              <div className="d-flex gap-3 align-items-center mt-1">
                <Form.Check
                  type="radio"
                  label="Feminino"
                  value="F"
                  id="sexo-f"
                  {...register('SEXOPAC')}
                />
                <Form.Check
                  type="radio"
                  label="Masculino"
                  value="M"
                  id="sexo-m"
                  {...register('SEXOPAC')}
                />
              </div>
            </Col>

            <Col md={3}>
              <Form.Label style={labelVermelho}>Fone Residencial</Form.Label>
              <Controller
                name="FONERESPAC"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Form.Control
                    {...field}
                    value={value || ''}
                    placeholder="(000)00000-0000"
                    style={inputVermelho}
                    onChange={(e) => {
                      const valorFormatado = mascaraTelefoneComZero(e.target.value);
                      onChange(valorFormatado); // Atualiza o estado do RHF já formatado
                    }}
                  />
                )}
              />
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Fone Comercial</Form.Label>
              <Form.Control
                {...register('FONECOMPAC')}
                placeholder="(000)00000-0000"
                style={inputBase}
              />
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Fone Recado</Form.Label>
              <Form.Control {...register('FONERECPAC')} style={inputBase} />
            </Col>

            <Col md={6}>
              <Form.Label style={labelCinza}>Contato para Recado</Form.Label>
              <Form.Control {...register('RAMALPAC')} style={inputBase} />
            </Col>

            <Col md={3}>
              <Form.Label style={labelVermelho}>Fone Celular</Form.Label>
              <Controller
                name="CELULARPAC"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Form.Control
                    {...field}
                    value={value || ''}
                    placeholder="(000)00000-0000"
                    style={inputVermelho}
                    onChange={(e) => {
                      const valorFormatado = mascaraTelefoneComZero(e.target.value);
                      onChange(valorFormatado); // Atualiza o estado do RHF já formatado
                    }}
                  />
                )}
              />
            </Col>

            <Col md={3}>
              <Form.Label style={labelCinza}>Operadora</Form.Label>
              <Form.Select {...register('CELULAROP')}>
                <option value="">Selecione...</option>
                <option value="VIVO">VIVO</option>
                <option value="CLARO">CLARO</option>
                <option value="TIM">TIM</option>
                <option value="OI">OI</option>
              </Form.Select>
            </Col>

            <Col md={12}>
              <Form.Label style={labelCinza}>E-mail do Paciente</Form.Label>
              <Form.Control {...register('EMAILPACIENTE')} style={inputBase} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* DADOS COMPLEMENTARES & MÉDICO & HISTÓRICO */}
      <Row className="gy-2">
        <Col md={7}>
          <Card
            style={{
              border: '1px solid var(--neutral-200)',
              borderRadius: 20,
              boxShadow: 'none',
              background: '#fff',
              overflow: 'hidden',
              marginBottom: '0.85rem',
            }}
            className="h-100"
          >
            <Card.Header
              className="d-flex align-items-center py-2"
              style={{ borderBottom: '1px solid var(--neutral-200)' }}
            >
              <h2 className="fs-6 mb-0 d-flex align-items-center gap-2">
                <FaRegClipboard aria-hidden="true" />
                Complementares
              </h2>
            </Card.Header>
            <Card.Body className="p-3">
              <Row className="gy-2">
                <Col md={4}>
                  <Form.Label style={labelCinza}>Estado Civil</Form.Label>
                  <Form.Select {...register('ESTCIVIL')}>
                    <option value="">Selecione...</option>
                    <option value="CASADO(A)">CASADO(A)</option>
                    <option value="SOLTEIRO(A)">SOLTEIRO(A)</option>
                    <option value="DIVORCIADO(A)">DIVORCIADO(A)</option>
                    <option value="VIUVO(A)">VIÚVO(A)</option>
                    <option value="UNIÃO ESTÁVEL">UNIÃO ESTÁVEL</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label style={labelCinza}>Cor</Form.Label>
                  <Form.Select {...register('CORPAC')}>
                    <option value="">Selecione...</option>
                    <option value="BRANCO">BRANCO</option>
                    <option value="PARDO">PARDO</option>
                    <option value="NEGRO">NEGRO</option>
                    <option value="AMARELO">AMARELO</option>
                    <option value="INDÍGENA">INDÍGENA</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label style={labelCinza}>Nacionalidade</Form.Label>
                  <Form.Select {...register('NACIONPAC')}>
                    <option value="BRASILEIRA">BRASILEIRA</option>
                    <option value="ESTRANGEIRA">ESTRANGEIRA</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label style={labelCinza}>Status</Form.Label>
                  <Form.Select {...register('STATUS')}>
                    <option value="ATIVO">ATIVO</option>
                    <option value="INATIVO">INATIVO</option>
                    <option value="BLOQUEADO">BLOQUEADO</option>
                  </Form.Select>
                </Col>

                <Col md={8}>
                  <Form.Label style={labelAzul}>Médico do Paciente</Form.Label>
                  <Form.Select {...register('CODMEDICO')} style={inputAzul}>
                    <option value="">Selecione o médico...</option>
                    {medicos.map((medico) => (
                      <option key={medico.codMed || medico.id} value={medico.codMed || medico.id}>
                        {medico.nome || medico.apelido}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label style={{ ...labelAzul, marginBottom: 0 }}>OBS:</Form.Label>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      className="rounded-pill d-inline-flex align-items-center gap-1"
                      onClick={toggleInfopac}
                      aria-expanded={mostrarInfopac}
                      aria-controls="campo-infopac"
                      title={
                        temInfopac && !mostrarInfopac
                          ? 'Este paciente possui informação em INFO'
                          : undefined
                      }
                    >
                      {mostrarInfopac ? (
                        <>
                          <FaChevronUp size={10} aria-hidden="true" />
                          Ocultar INFO
                        </>
                      ) : (
                        <>
                          INFO
                          {temInfopac && (
                            <Badge
                              bg="danger"
                              pill
                              className="ms-1"
                              style={{ fontSize: '0.65rem' }}
                            >
                              •
                            </Badge>
                          )}
                        </>
                      )}
                    </Button>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    {...register('OBS')}
                    placeholder="Observações gerais do cadastro…"
                    style={inputVermelho}
                  />
                  <Collapse in={mostrarInfopac}>
                    <div id="campo-infopac" className="mt-2">
                      <Form.Label style={labelVermelho}>INFO:</Form.Label>
                      <p className="text-muted small mb-1">
                        Visível apenas para quem expandir este campo.
                      </p>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        {...infopacField}
                        ref={(element) => {
                          infopacField.ref(element);
                          infopacTextareaRef.current = element;
                        }}
                        placeholder="Informação restrita da equipe (INFO)…"
                        style={inputVermelho}
                      />
                    </div>
                  </Collapse>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* TABELA DE AGENDAMENTOS (PRONTUÁRIO ELETRÔNICO) */}
        <Col md={5}>
          <Card
            style={{
              border: '1px solid var(--neutral-200)',
              borderRadius: 20,
              boxShadow: 'none',
              background: '#fff',
              overflow: 'hidden',
              marginBottom: '0.85rem',
            }}
            className="h-100"
          >
            <Card.Header
              className="d-flex align-items-center py-2"
              style={{ borderBottom: '1px solid var(--neutral-200)' }}
            >
              <h2 className="fs-6 mb-0 d-flex align-items-center gap-2">
                <FaLaptopMedical aria-hidden="true" />
                Prontuário Eletrônico
              </h2>
            </Card.Header>
            <Card.Body className="p-2">
              <div
                style={{
                  maxHeight: '250px',
                  overflow: 'hidden',
                  border: '1px solid var(--neutral-200)',
                  borderRadius: 10,
                }}
              >
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0 align-middle"
                  style={{
                    width: '100%',
                    fontSize: '0.78rem',
                    border: 0,
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                  }}
                >
                  <thead className="table-light sticky-top">
                    <tr>
                      <th
                        style={{
                          borderLeft: 0,
                          borderTop: 0,
                          borderBottom: '1px solid var(--neutral-200)',
                        }}
                      >
                        Agenda
                      </th>
                      <th
                        style={{
                          borderTop: 0,
                          borderBottom: '1px solid var(--neutral-200)',
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          borderRight: 0,
                          borderTop: 0,
                          borderBottom: '1px solid var(--neutral-200)',
                        }}
                      >
                        Procedi
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agendamentosPaciente?.length > 0 ? (
                      agendamentosPaciente.map((item, idx) => (
                        <tr key={idx}>
                          <td className="text-nowrap">{item.DATAAGENDA_FORMATADA}</td>
                          <td>
                            <span
                              className={`badge ${item.SITUACAO === 'Atendido' ? 'bg-primary' : 'bg-secondary'}`}
                            >
                              {item.SITUACAO}
                            </span>
                          </td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: '140px' }}
                            title={item.PROCEDIMENTO}
                          >
                            {item.PROCEDIMENTO}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center text-muted py-3">
                          Nenhum agendamento anterior encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
